import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "actionsMenuProps", "buttonWrapperProps"];
import { Children, useCallback, useEffect, useRef, useState } from 'react';
import { ButtonWrapper } from '../../transitional/components/ButtonWrapper';
import unescapedText from 'I18n/utils/unescapedText';
import { HiddenMeasure } from '../../transitional/utils/HiddenMeasure';
import { ActionsCollapserProvider, useActionsCollapserContext } from '../../transitional/components/ActionsCollapser/ActionsCollapserProvider';
import { deepMerge } from '../../transitional/utils/deepMerge/deepMerge';
import { filterProps } from '../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../hooks/useMapTokenPropsToCSS';
import styled from 'foundations-theming/utils';
import { ChildrenActionsMenu } from '../../components/ActionsMenu/ChildrenActionsMenu';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getElementWidth = el => {
  const rect = el.getBoundingClientRect();
  const computedStyles = getComputedStyle(el);
  return rect.width + parseFloat(computedStyles.marginLeft) + parseFloat(computedStyles.marginRight);
};
const getAvailableWidth = (parentEl, outerEl) => {
  const computedStyles = getComputedStyle(parentEl, null);
  if (!computedStyles) {
    return Infinity;
  }
  const innerWidth = getElementWidth(parentEl) - parseFloat(computedStyles.paddingLeft) - parseFloat(computedStyles.paddingRight);
  let availableWidth = innerWidth;
  if (computedStyles.display === 'flex' && computedStyles.flexDirection === 'row' && computedStyles.flexWrap === 'nowrap') {
    const childNodes = parentEl.childNodes;
    for (let i = 0; i < childNodes.length; i++) {
      if (childNodes[i] !== outerEl) {
        availableWidth -= getElementWidth(childNodes[i]);
      }
    }
  }
  return availableWidth;
};
const MoreActionsDropdown = ({
  actionsMenuStyleOverrides,
  actionsMenuProps,
  largestElementWidth,
  excessActions
}) => {
  return /*#__PURE__*/_jsx(ActionsCollapserProvider, {
    actionsMenuStyleOverrides: actionsMenuStyleOverrides,
    children: /*#__PURE__*/_jsx(ChildrenActionsMenu, Object.assign({
      use: "tertiary-light",
      "data-more-tabs": true,
      referenceText: unescapedText('salesUI.UITabs.moreDropdownLabel'),
      styleOverrides: actionsMenuStyleOverrides,
      "aria-label": "More",
      menuInlineSize: largestElementWidth
    }, actionsMenuProps, {
      children: excessActions
    }))
  });
};
const StyledDiv = styled.div.withConfig({
  displayName: "ActionsCollapser__StyledDiv",
  componentId: "l8gucf-0"
})(({
  $cssStyles
}) => {
  return $cssStyles;
});
export function ActionsCollapser(_ref) {
  var _actionsCollapserCont, _actionsCollapserCont2;
  let {
      children,
      actionsMenuProps,
      buttonWrapperProps
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [excessActionsCount, setExcessActionsCount] = useState(0);
  const [largestElementWidth, setLargestElementWidth] = useState(0);
  const childArray = Children.toArray(children);
  const _outerEl = useRef(null);
  const _measureEl = useRef(null);
  const _resizeObserver = useRef(null);
  const actionsCollapserContext = useActionsCollapserContext();
  const actionsMenuStyleOverrides = deepMerge({
    inlineSize: 'fit-content'
  }, (_actionsCollapserCont = actionsCollapserContext === null || actionsCollapserContext === void 0 ? void 0 : actionsCollapserContext.actionsMenuStyleOverrides) !== null && _actionsCollapserCont !== void 0 ? _actionsCollapserCont : {});
  const buttonWrapperStyleOverrides = deepMerge({
    columnGap: 0
  }, (_actionsCollapserCont2 = actionsCollapserContext === null || actionsCollapserContext === void 0 ? void 0 : actionsCollapserContext.buttonWrapperStyleOverrides) !== null && _actionsCollapserCont2 !== void 0 ? _actionsCollapserCont2 : {});
  const stopObserver = useCallback(() => {
    var _resizeObserver$curre;
    (_resizeObserver$curre = _resizeObserver.current) === null || _resizeObserver$curre === void 0 || _resizeObserver$curre.disconnect();
  }, [_resizeObserver]);
  useEffect(() => {
    function getExcessActionsCount() {
      const measureEl = _measureEl.current;
      const outerEl = _outerEl && _outerEl.current ? _outerEl.current.parentElement : null;
      const parentEl = outerEl ? outerEl.parentElement : null;
      if (measureEl == null || outerEl == null || parentEl == null) {
        return;
      }
      const horizontalGap = _outerEl.current ? Math.abs(parseInt(getComputedStyle(_outerEl.current).marginLeft, 10)) : 0;
      const containerWidth = Math.ceil(getAvailableWidth(parentEl, outerEl));
      let totalActionsWidth = 0;
      let blockScopedlargestElementWidth = 0;
      const totalActionsArray = Array.from(measureEl.children).filter(action => !action.getAttribute('data-more-tabs'));

      // Don't add in the horizontal gap for zero-width elements
      const getElementWidthPlusHorizontalGap = element => {
        let elementWidth = getElementWidth(element);
        if (elementWidth > 0) {
          elementWidth += horizontalGap;
        }
        return elementWidth;
      };
      totalActionsArray.forEach(element => {
        const elementWidth = getElementWidthPlusHorizontalGap(element);
        totalActionsWidth += elementWidth;
        if (elementWidth + horizontalGap > blockScopedlargestElementWidth) {
          // add horizontalGap otherwise the right padding will be cut off
          blockScopedlargestElementWidth = elementWidth + horizontalGap;
        }
      });
      setLargestElementWidth(blockScopedlargestElementWidth);
      if (totalActionsWidth <= containerWidth) {
        if (excessActionsCount !== 0) {
          setExcessActionsCount(0);
        }
        return;
      }

      /**
       * UIActionsCollapser uses querySelector on document rather than the outerEl but this is probably a mistake
       * @see https://git.hubteam.com/HubSpot/UIComponents/blob/f8a0f921b6d718c143fd06142cbe9c8080444a0e/UIComponents/static/js/layout/UIActionsCollapser.js#L131-L134
       */
      const moreActionsDropdownElement = outerEl.querySelector('[data-more-tabs]');
      const moreActionsDropdownWidth = moreActionsDropdownElement ? getElementWidth(moreActionsDropdownElement) + horizontalGap : 0;
      let calcActionsWidth = moreActionsDropdownWidth;
      let newExcessActionsCount = totalActionsArray.length;
      for (let i = 0; i < totalActionsArray.length; i++) {
        const actionWidth = getElementWidthPlusHorizontalGap(totalActionsArray[i]);
        if (calcActionsWidth + actionWidth > containerWidth) {
          break;
        }
        calcActionsWidth += actionWidth;
        newExcessActionsCount -= 1;
      }
      if (newExcessActionsCount !== excessActionsCount) {
        setExcessActionsCount(newExcessActionsCount);
      }
    }
    if (_outerEl.current) {
      var _outerEl$current;
      _resizeObserver.current = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          getExcessActionsCount();
        });
      });
      const parentElement = (_outerEl$current = _outerEl.current) === null || _outerEl$current === void 0 || (_outerEl$current = _outerEl$current.parentElement) === null || _outerEl$current === void 0 ? void 0 : _outerEl$current.parentElement;
      if (parentElement) {
        _resizeObserver.current.observe(parentElement);
      }
      if (_measureEl.current) {
        _resizeObserver.current.observe(_measureEl.current);
      }
    }
    return stopObserver;
  }, [stopObserver, excessActionsCount]);
  const getExcessActions = () => childArray.slice(childArray.length - excessActionsCount);
  const getVisibleActions = () => childArray.slice(0, childArray.length - excessActionsCount);
  const visibleActions = getVisibleActions();
  const excessActions = getExcessActions();
  const excessActionsDropdown = /*#__PURE__*/_jsx(MoreActionsDropdown, {
    actionsMenuStyleOverrides: actionsMenuStyleOverrides,
    actionsMenuProps: actionsMenuProps,
    largestElementWidth: largestElementWidth,
    excessActions: excessActions
  });
  buttonWrapperProps = Object.assign({}, buttonWrapperProps, {
    flexRef: _outerEl
  });
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);

  // We need to wrap children in an element with display:inline-block to measure the width of actions individually
  const spanWrappedChildren = Children.map(children, (child, index) => {
    return /*#__PURE__*/_jsx("span", {
      style: {
        display: 'inline-block'
      },
      children: child
    }, index);
  });
  return /*#__PURE__*/_jsx(StyledDiv, Object.assign({
    $cssStyles: cssStyles
  }, domProps, {
    children: /*#__PURE__*/_jsx(ActionsCollapserProvider, {
      buttonWrapperStyleOverrides: buttonWrapperStyleOverrides,
      children: /*#__PURE__*/_jsxs(ButtonWrapper, Object.assign({}, buttonWrapperProps, {
        children: [visibleActions, excessActionsCount > 0 && excessActionsDropdown, /*#__PURE__*/_jsxs(HiddenMeasure, {
          ref: _measureEl,
          children: [spanWrappedChildren, excessActionsDropdown]
        })]
      }))
    })
  }));
}